import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../auth/PermissionsContext';
import LoadingPage from './LoadingPage';

/**
 * PermissionGuard - permission-based route guard.
 *
 * Requires explicit permission props. Does NOT auto-detect from route path.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to protect
 * @param {string} [props.permission] - Single required permission
 * @param {string[]} [props.anyOf] - Array of permissions (needs at least one)
 * @param {string[]} [props.allOf] - Array of permissions (needs all)
 * @param {string} [props.redirectTo='/forbidden'] - Redirect path on denial
 */
const PermissionGuard = ({
  children,
  permission,
  anyOf,
  allOf,
  redirectTo = '/forbidden'
}) => {
  const location = useLocation();
  const { has, hasAny, hasAll, isInitialized, isAuthenticated } = usePermissions();

  if (!isInitialized) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If no permission props provided, allow access
  if (!permission && !anyOf && !allOf) {
    return children;
  }

  let hasPermission = false;

  if (permission) {
    hasPermission = has(permission);
  } else if (anyOf && Array.isArray(anyOf)) {
    hasPermission = hasAny(anyOf);
  } else if (allOf && Array.isArray(allOf)) {
    hasPermission = hasAll(allOf);
  }

  if (!hasPermission) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return children;
};

export default PermissionGuard;
